import * as React from "react";
import Button from "@mui/material/Button"
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField"
import Title from "../Common/Title";


export default function IssueList({ onDeployToModel, token }) {
    const constructIssue = (id_issue, id_issue_given, id_solution, title, description, time_updated, origin, acl) => {
        return { id_issue, id_issue_given, id_solution, title, description, time_updated, origin, acl };
    }

    const [Issues, setIssues] = React.useState([]);
    const [LimitIssues, setLimitIssues] = React.useState(25);

    const [SearchString, setSearchString] = React.useState("");

    const [OrderBy, setOrderBy] = React.useState("time_updated");
    const [OrderDirection, setOrderDirection] = React.useState("DESC");

    const [DialogOpen, setDialogOpen] = React.useState(false);
    const [DialogText, setDialogText] = React.useState("");

    /*
     * Event Handlers
     */
    const handleSearchString = (event) => {
        const value = event.target.value;
        setSearchString(value);
    };

    const handleOrder = (columnName) => {
        if (OrderBy === columnName) {
            setOrderDirection(OrderDirection === "ASC" ? "DESC" : "ASC");
        } else {
            setOrderBy(columnName);
            setOrderDirection("ASC");
        }
    };

    const OrderDirectionTriangle = ({ column }) => {
        if (OrderBy === column) {
            return OrderDirection === "ASC" ? " ▲" : " ▼";
        }
        return null;
    };

    const columnStyles = {
        idIssue: { width: '10%', cursor: "pointer" },
        title: { width: '40%', cursor: "pointer" },
        timeUpdated: { width: '20%', cursor: "pointer" },
        origin: { width: '10%', cursor: "pointer" },
        acl: { width: '5%', cursor: "pointer" },
        actions: { width: '15%' } // Assuming actions may need more space
    };

    const fetchIssues = async () => {
        const response = fetch(`${process.env.REACT_APP_API_URL}/issues/listlast/${LimitIssues}`, 
            {
                method: "POST", 
                headers: {
                    "Content-Type": "application/json",
                    "Token": token
                },
                body: JSON.stringify({
                    search_string: (SearchString !== "") ? SearchString : null, 
                    order_by: OrderBy, 
                    order_direction: OrderDirection
                })
            }
        )
        .then((response) => response.json())
        .then((data) => {
            const transformedData = data.map(([id_issue, id_issue_given, id_solution, title, description, time_updated, origin, acl]) => {
                // Function to format dates by removing milliseconds
                const formatDate = (dateString) => {
                    if (!dateString) return null; // Return null if dateString is null
                    const date = new Date(dateString);
                    return `${date.toLocaleString().split(".")[0]}`; // Remove milliseconds and retain the "Z" timezone indicator
                };
            
                // Apply formatting to time_start and time_end
                const formattedTimeUpdated = formatDate(time_updated);
            
                // Return the session object with formatted times
                return constructIssue(id_issue, id_issue_given, id_solution, title, description, formattedTimeUpdated, origin, acl);
                });
                // Update the sessions state with the transformed and formatted data
                setIssues(transformedData);
        })
        .catch((error) => console.error("Error fetching issues:", error));
    }

    React.useEffect(() => {
        fetchIssues();
    }, [LimitIssues, SearchString, OrderBy, OrderDirection]);

    const deployToModel = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/structured/deploy`, 
                { 
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Token": token
                    }
                }
            );
            const data = await response.json();
                if (response.status === 423) {
                    setDialogText("Error: Resource is locked.")
                    setDialogOpen(true);
                    return;
                }
            // Assuming you might want to use the response data for something
            console.log("Deployment Successful", data);
            setDialogText("Structured database successfully deployed to AI.")
            setDialogOpen(true); // Open the dialog here
            onDeployToModel();
        } catch (error) {
            console.error("Deployment failed:", error);
            // Handle error case, potentially setting a different dialog content or another state variable indicating the error
        }
    };

    return (
        <React.Fragment>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Title>Issues</Title>
                </Grid>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <Button
                            onClick={() => { window.location.href = `/console/structured/issue/new` }}
                            variant="contained"
                            color="primary"
                            >
                            New Issue
                            </Button>
                        </Grid>
                        <Grid item>
                            <TextField
                            margin="normal"
                            id="search"
                            label="Search"
                            name="search"
                            value={ SearchString }
                            onChange={ handleSearchString }
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <Button
                            onClick={() => { deployToModel() }}
                            variant="contained"
                            color="error"
                            >
                            Deploy to Model
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell style={columnStyles.idIssue} onClick={() => handleOrder("id_issue")}>
                            ID<OrderDirectionTriangle column="id_issue" />
                        </TableCell>
                        <TableCell style={columnStyles.title} onClick={() => handleOrder("title")}>
                            Title<OrderDirectionTriangle column="title" />
                        </TableCell>
                        <TableCell style={columnStyles.timeUpdated} onClick={() => handleOrder("time_updated")}>
                            Time Updated<OrderDirectionTriangle column="time_updated" />
                        </TableCell>
                        <TableCell style={columnStyles.origin} onClick={() => handleOrder("origin")}>
                            Origin<OrderDirectionTriangle column="origin" />
                        </TableCell>
                        <TableCell style={columnStyles.acl}>
                            Roles
                        </TableCell>
                        <TableCell style={columnStyles.actions}></TableCell> {/* Actions column header */}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Issues.map((issue) => (
                        <TableRow key={issue.id_issue}> {/* Ensure each row has a unique key */}
                            <TableCell>{issue.id_issue}</TableCell>
                            <TableCell>{issue.title}</TableCell>
                            <TableCell>{issue.time_updated}</TableCell>
                            <TableCell>{issue.origin}</TableCell>
                            <TableCell>{issue.acl}</TableCell>
                            <TableCell>
                                {/* Button to access session log, adjust the onClick function as needed */}
                                <Button
                                    variant="text"
                                    onClick={() => { window.location.href = `/console/structured/issue/${issue.id_issue}` }}
                                >
                                    Edit Issue
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Link color="primary" onClick={() => setLimitIssues(LimitIssues + 25)} sx={{ mt: 3 }}>
                Load more
            </Link>

            <Dialog
            open={DialogOpen}
            onClose={() => setDialogOpen(false)}
            aria-labelledby="deployment-dialog-title"
            aria-describedby="deployment-dialog-description"
            >
            <DialogTitle id="deployment-dialog-title">{"Deployment Status"}</DialogTitle>
            <DialogContent>
                <DialogContentText id="deployment-dialog-description">
                { DialogText }
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDialogOpen(false)}>Close</Button>
            </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}